import React, { useState, useEffect } from "react";
import { useUIStore } from "../../store/useUIStore";
import { useSupportStore } from "../../store/useSupportStore";
import { useEnhancedNodeOperations } from "./hooks/operations/useEnhancedNodeOperations";
import { EnhancedNodeContextMenu } from "../ui/EnhancedNodeContextMenu";
import { DraggableCoordinateInputModal } from "./DraggableCoordinateInputModal";
import { SupportConfigDialog } from "../ui/SupportConfigDialog";
import { useTrussStore } from "../../hooks/useTrussManagement";
import PredefinedLoadManager from "../loading/PredefinedLoadManager";
import {
  SupportPosition,
  SupportType,
  DegreesOfFreedom,
  DEFAULT_DOF_CONFIGS,
  SUPPORT_VISUAL_CONFIGS,
} from "../../types/support";

/**
 * Enhanced Node Interaction Components
 * Integrates context menu and support functionality
 */
export const EnhancedNodeInteractionComponents: React.FC = () => {
  const uiStore = useUIStore();
  const supportStore = useSupportStore();
  const groupedTrusses = useTrussStore((s) => s.getGroupedTruss());
  const { handleMoveNode } = useEnhancedNodeOperations();
  const { applyLoadOnNode } = useTrussStore();

  const [supportDialogVisible, setSupportDialogVisible] = useState(false);
  const [supportDialogNodeId, setSupportDialogNodeId] = useState<string | null>(
    null
  );
  const [addLoadDialogVisible, setAddLoadDialogVisible] = useState(false);
  const [targetNodeIds, setTargetNodeIds] = useState<string | null>(null);

  // ✅ Inline mode: "beam" | "node" | null
  const [inlineMode, setInlineMode] = useState<"beam" | "node" | null>(null);

  const handleAddPredefinedLoadClick = (type: "beam" | "node") => {
    const targetIds = uiStore.enhancedNodeContextMenuNodeId;
    setAddLoadDialogVisible(true);
    setInlineMode(type);
    setTargetNodeIds(targetIds);
    setTimeout(() => uiStore.closeEnhancedNodeContextMenu(), 100);
  };

  // 🔹 Add Support Dialog Handling
  const handleAddSupport = (nodeId: string) => {
    setSupportDialogNodeId(nodeId);
    setSupportDialogVisible(true);
    uiStore.closeEnhancedNodeContextMenu();
  };

  // const handleSupportConfirm = (
  //   nodeId: string,
  //   position: SupportPosition,
  //   type: SupportType,
  //   dof?: DegreesOfFreedom
  // ) => {
  //   const newSupport = supportStore.addSupport(nodeId, position, type);
  //   if (dof) supportStore.updateSupport(newSupport.id, { dof });
  // };
  // Handle support configuration confirmation for multiple nodes
  const handleSupportConfirm = (
    nodeIds: string[],
    position: SupportPosition,
    type: SupportType,
    dof?: DegreesOfFreedom
  ) => {
    const finalDOF = dof || DEFAULT_DOF_CONFIGS[type];
    nodeIds.forEach((nodeId) => {
      // Check if node already has a support (only one support per node allowed)
      const existingSupports = supportStore.getSupportsByNodeId(nodeId);

      if (existingSupports.length > 0) {
        // Update existing support
        const existingSupport = existingSupports[0];

        supportStore.updateSupport(existingSupport.id, {
          position,
          type,
          dof: finalDOF,
          color: SUPPORT_VISUAL_CONFIGS[type].color, // Update color based on new type
        });
      } else {
        // Add new support
        const newSupport = supportStore.addSupport(nodeId, position, type);
        if (dof) supportStore.updateSupport(newSupport.id, { dof });
        // Update DOF - use provided custom DOF for fixed supports, or default for other types
        supportStore.updateSupport(newSupport.id, { dof: finalDOF });

        console.log(
          `Added ${type} support ${position} to node ${nodeId} with DOF:`,
          finalDOF
        );
      }
    });

    setSupportDialogVisible(false);
    setSupportDialogNodeId(null);
  };

  const handleSupportCancel = () => {
    setSupportDialogVisible(false);
    setSupportDialogNodeId(null);
  };

  const handleApplyPredefinedTruss = (preset: any) => {
    const nodeId = uiStore.enhancedNodeContextMenuNodeId;
    if (!nodeId) return;

    applyLoadOnNode(
      [nodeId],
      preset.rotationValues,
      preset.perpendicularValues
    );

    uiStore.closeEnhancedNodeContextMenu();
  };

  // 🔹 Context Menu Items
  const contextMenuItems = [
    {
      id: "add-load",
      label: "Add Load",
      icon: "📦",
      submenu: [
        {
          id: "add-node-load",
          label: "Add Node Load",
          icon: "➕",
          action: () => handleAddPredefinedLoadClick("node"),
        },
        {
          id: "predefined-loads",
          label: "Predefined Loads",
          icon: "🧩",
          submenu:
            groupedTrusses.length > 0
              ? groupedTrusses.map((preset) => ({
                  id: preset.id,
                  label: preset.name,
                  icon: "🧩",
                  action: () => handleApplyPredefinedTruss(preset),
                }))
              : [
                  {
                    id: "none",
                    label: "No saved loads",
                    action: () => {},
                  },
                ],
        },
      ],
    },
    {
      id: "support",
      label: "Add Support",
      icon: "🏗️",
      action: () => {
        if (uiStore.enhancedNodeContextMenuNodeId) {
          handleAddSupport(uiStore.enhancedNodeContextMenuNodeId);
        }
      },
    },
    {
      id: "move-node",
      label: "Move Node",
      icon: "📐",
      action: () => {
        if (uiStore.enhancedNodeContextMenuNodeId) {
          handleMoveNode(uiStore.enhancedNodeContextMenuNodeId);
        }
      },
    },
  ];

  return (
    <>
      {/* ✅ Context Menu */}
      <EnhancedNodeContextMenu
        x={uiStore.enhancedNodeContextMenuPosition.x}
        y={uiStore.enhancedNodeContextMenuPosition.y}
        visible={uiStore.enhancedNodeContextMenuVisible}
        items={contextMenuItems}
        onClose={() => uiStore.closeEnhancedNodeContextMenu()}
      />

      {/* ✅ Move Node Modal */}
      <DraggableCoordinateInputModal
        isOpen={uiStore.nodeMoveDialogVisible}
        nodeId={uiStore.nodeMoveDialogNodeId}
        onClose={() => uiStore.closeNodeMoveDialog()}
      />

      {/* ✅ Support Dialog */}
      <SupportConfigDialog
        visible={supportDialogVisible}
        nodeId={supportDialogNodeId}
        onConfirm={handleSupportConfirm}
        onCancel={handleSupportCancel}
      />

      {/* ✅ Predefined Load Manager (for node inline mode) */}
      {addLoadDialogVisible && (
        <PredefinedLoadManager
          nodeId={targetNodeIds ?? undefined}
          inlineMode={inlineMode}
          setInlineMode={setInlineMode}
        />
      )}
    </>
  );
};

export default EnhancedNodeInteractionComponents;
